export interface JobRequirement {
  category: string;
  items: string[];
}

export interface JobBenefit {
  icon: string;
  title: string;
  description: string;
}

export interface JobResponsibility {
  title: string;
  description: string;
}

export interface Job {
  id: string;
  category: string;
  categoryColor: "purple" | "coral" | "mixed";
  title: string;
  payRange: string;
  locationType: string;
  schedule: string;
  description: string;
  skills: string[];
  // Extended fields for individual job pages
  longDescription: string;
  overview: string;
  responsibilities: JobResponsibility[];
  requirements: JobRequirement[];
  preferredQualifications: string[];
  benefits: JobBenefit[];
  workingConditions: string[];
  growthOpportunities: string[];
  teamInfo: {
    size: string;
    structure: string;
    collaboration: string;
  };
  applicationProcess: string[];
  faqs: {
    question: string;
    answer: string;
  }[];
  estimatedHoursPerWeek: string;
  projectTypes: string[];
  toolsUsed: string[];
  trainingProvided: string[];
  performanceMetrics: string[];
}

export const jobsData: Job[] = [
  {
    id: "ai-trainer",
    category: "AI Training",
    categoryColor: "purple",
    title: "AI Trainer",
    payRange: "$25-45/hr",
    locationType: "Remote",
    schedule: "Flexible Hours",
    description:
      "Help train AI models by providing high-quality feedback on responses, evaluating accuracy, and improving AI reasoning capabilities.",
    skills: ["Critical Thinking", "AI Knowledge", "English Fluency"],
    longDescription:
      "As an AI Trainer at Unimerg, you will play a crucial role in shaping the future of artificial intelligence. You'll work directly with cutting-edge AI models, providing the human intelligence and feedback that helps these systems learn, improve, and become more helpful to users worldwide. This role combines analytical thinking with creativity, as you'll evaluate AI responses across a wide range of topics and scenarios, identifying areas for improvement and helping to refine the model's understanding of nuance, context, and accuracy. Your work directly impacts millions of users who interact with AI systems daily, making this one of the most impactful roles in the AI industry today.",
    overview:
      "AI Trainers are the human experts behind the intelligence in artificial intelligence. You'll evaluate, rate, and improve AI-generated content across various domains, ensuring that AI systems provide accurate, helpful, and safe responses. This role is perfect for curious minds who enjoy problem-solving, have strong communication skills, and want to be at the forefront of AI development.",
    responsibilities: [
      {
        title: "Evaluate AI Responses",
        description:
          "Assess AI-generated content for accuracy, relevance, helpfulness, and safety. Provide detailed ratings and feedback that helps improve model performance.",
      },
      {
        title: "Provide Quality Feedback",
        description:
          "Write clear, constructive feedback explaining why certain responses are better than others, helping AI systems understand nuance and context.",
      },
      {
        title: "Create Training Data",
        description:
          "Generate high-quality prompts and responses that serve as examples for AI learning, covering diverse topics and scenarios.",
      },
      {
        title: "Identify Edge Cases",
        description:
          "Discover and document scenarios where AI responses fall short, helping to identify areas that need improvement.",
      },
      {
        title: "Maintain Consistency",
        description:
          "Follow established guidelines and rubrics to ensure consistent evaluation standards across all tasks.",
      },
      {
        title: "Collaborate with Teams",
        description:
          "Work with other trainers and project managers to discuss challenging cases and refine evaluation criteria.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "Bachelor's degree in any field, or equivalent work experience",
          "Strong academic background with demonstrated analytical abilities",
          "Experience with writing, editing, or content creation preferred",
          "Familiarity with AI concepts and large language models is a plus",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Excellent written communication skills in English",
          "Ability to evaluate complex information quickly and accurately",
          "Comfortable working with web-based tools and platforms",
          "Basic understanding of how AI and machine learning work",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Strong critical thinking and analytical reasoning",
          "Attention to detail and commitment to quality",
          "Self-motivated with ability to work independently",
          "Adaptable to changing guidelines and requirements",
          "Excellent time management skills",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Reliable high-speed internet connection (minimum 10 Mbps)",
          "Modern computer with updated browser (Chrome, Firefox, or Safari)",
          "Quiet workspace for focused work",
          "Availability for at least 15-20 hours per week",
        ],
      },
    ],
    preferredQualifications: [
      "Graduate degree in linguistics, cognitive science, computer science, or related field",
      "Previous experience in content moderation, editing, or quality assurance",
      "Knowledge of multiple languages",
      "Experience with AI tools like ChatGPT, Claude, or similar",
      "Background in research or academic writing",
      "Understanding of bias, fairness, and ethics in AI",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Competitive Pay",
        description:
          "Earn $25-45/hr based on expertise and performance, with regular opportunities for rate increases.",
      },
      {
        icon: "Clock",
        title: "Flexible Schedule",
        description:
          "Work when it suits you—early morning, late night, or anywhere in between. You control your hours.",
      },
      {
        icon: "MapPin",
        title: "Fully Remote",
        description:
          "Work from anywhere in the world. No commute, no office politics—just meaningful work from your preferred location.",
      },
      {
        icon: "TrendingUp",
        title: "Career Growth",
        description:
          "Advance to senior trainer, team lead, or specialized roles with increased responsibilities and pay.",
      },
      {
        icon: "BookOpen",
        title: "Continuous Learning",
        description:
          "Access free training, certifications, and resources to develop your skills in AI and related fields.",
      },
      {
        icon: "Users",
        title: "Global Community",
        description:
          "Join a diverse community of 50,000+ contributors from 10+ countries, sharing knowledge and experiences.",
      },
    ],
    workingConditions: [
      "100% remote position—work from anywhere with reliable internet",
      "Flexible scheduling—choose your own hours within project deadlines",
      "Task-based work—complete tasks at your own pace",
      "Regular feedback and performance reviews",
      "Access to online community and support channels",
      "No minimum hours required, but consistent availability preferred",
    ],
    growthOpportunities: [
      "Senior AI Trainer: Lead complex evaluation projects with higher pay rates",
      "Quality Reviewer: Review and calibrate work from other trainers",
      "Team Lead: Manage a team of trainers and coordinate projects",
      "Specialized Trainer: Focus on specific domains like medical, legal, or technical content",
      "Training Developer: Help create training materials and guidelines",
      "Full-time Employment: Top performers may be offered full-time positions",
    ],
    teamInfo: {
      size: "You'll be part of a global team of 5,000+ AI Trainers",
      structure:
        "Trainers work independently but are supported by project managers, quality reviewers, and technical support staff",
      collaboration:
        "Regular team meetings, Slack channels for questions, and peer collaboration on challenging tasks",
    },
    applicationProcess: [
      "Submit your application with resume and brief introduction",
      "Complete an online assessment testing critical thinking and writing skills",
      "Participate in a calibration exercise to demonstrate evaluation abilities",
      "Attend a virtual onboarding session (1-2 hours)",
      "Complete certification training for specific project types",
      "Begin working on live tasks with mentor support",
    ],
    faqs: [
      {
        question: "How many hours do I need to work?",
        answer:
          "There's no strict minimum, but we recommend at least 15-20 hours per week to maintain proficiency and access to the best projects. You can work more during busy periods.",
      },
      {
        question: "How quickly will I get paid?",
        answer:
          "Payments are processed weekly every Friday for work completed through the previous Sunday. Most payment methods receive funds within 3-5 business days.",
      },
      {
        question: "Do I need AI experience?",
        answer:
          "No prior AI experience is required. We provide comprehensive training on AI concepts and evaluation techniques. Strong critical thinking and communication skills are more important.",
      },
      {
        question: "Can I work on multiple projects?",
        answer:
          "Yes! Once you're certified, you can work on multiple project types simultaneously, increasing your earning potential and variety of work.",
      },
    ],
    estimatedHoursPerWeek: "15-40 hours (flexible)",
    projectTypes: [
      "Response quality evaluation",
      "Comparative ranking of AI outputs",
      "Factual accuracy verification",
      "Safety and policy compliance review",
      "Creative writing assessment",
      "Technical accuracy evaluation",
    ],
    toolsUsed: [
      "Unimerg Training Platform",
      "Custom evaluation interfaces",
      "Collaboration tools (Slack, Google Meet)",
      "Documentation systems",
      "Quality tracking dashboards",
    ],
    trainingProvided: [
      "AI Fundamentals course (2 hours)",
      "Evaluation methodology training (4 hours)",
      "Project-specific guidelines and rubrics",
      "Ongoing calibration sessions",
      "Access to knowledge base and FAQs",
    ],
    performanceMetrics: [
      "Accuracy score (agreement with expert reviewers)",
      "Consistency rating (alignment with guidelines)",
      "Productivity (tasks completed per hour)",
      "Quality feedback ratings",
      "Timeliness of task completion",
    ],
  },
  {
    id: "data-annotator",
    category: "Data Annotation",
    categoryColor: "coral",
    title: "Data Annotator",
    payRange: "$18-30/hr",
    locationType: "Remote",
    schedule: "Part Time",
    description:
      "Label and annotate images, text, audio, and video data to help machine learning models understand and categorize information accurately.",
    skills: ["Attention to Detail", "Pattern Recognition", "Data Analysis"],
    longDescription:
      "Data Annotators are the foundation of machine learning. Every AI system that recognizes faces, understands speech, or drives autonomous vehicles learned from data that was carefully labeled by human annotators. As a Data Annotator at Unimerg, you'll work on diverse projects spanning computer vision, natural language processing, and audio recognition. Your precise labeling work directly enables AI systems to understand the world around them. This role is ideal for detail-oriented individuals who take pride in accuracy and want to contribute to groundbreaking AI applications across industries from healthcare to autonomous vehicles.",
    overview:
      "Data Annotators provide the labeled training data that machine learning models need to learn. You'll work with various data types—images, text, audio, and video—applying labels, drawing bounding boxes, transcribing speech, and categorizing content according to project specifications. Your accuracy directly impacts the quality of AI systems worldwide.",
    responsibilities: [
      {
        title: "Image Annotation",
        description:
          "Draw bounding boxes, polygons, and keypoints on images to identify objects, people, and features for computer vision models.",
      },
      {
        title: "Text Classification",
        description:
          "Categorize text content by sentiment, intent, topic, or other attributes to train natural language understanding models.",
      },
      {
        title: "Audio Labeling",
        description:
          "Transcribe speech, identify speakers, and label audio segments for speech recognition and audio analysis systems.",
      },
      {
        title: "Video Annotation",
        description:
          "Track objects across video frames, label actions and events, and annotate temporal sequences for video understanding AI.",
      },
      {
        title: "Quality Assurance",
        description:
          "Review your own work and that of peers to ensure annotations meet project quality standards.",
      },
      {
        title: "Documentation",
        description:
          "Report edge cases, ambiguous scenarios, and potential issues to help improve annotation guidelines.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "High school diploma or equivalent required",
          "Some college education preferred but not required",
          "No prior annotation experience necessary—we provide training",
          "Experience with data entry or detailed visual work is a plus",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Basic computer proficiency and comfort with web applications",
          "Ability to learn new software tools quickly",
          "Good visual perception and spatial reasoning",
          "Typing speed of at least 40 WPM for text tasks",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Exceptional attention to detail and accuracy",
          "Patience for repetitive but important tasks",
          "Ability to follow detailed instructions precisely",
          "Self-discipline to maintain quality over long sessions",
          "Good judgment for ambiguous cases",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Computer with minimum 8GB RAM and modern processor",
          "High-resolution monitor (1080p minimum, 4K preferred for image tasks)",
          "Reliable internet connection (minimum 10 Mbps)",
          "Headphones for audio annotation tasks",
        ],
      },
    ],
    preferredQualifications: [
      "Experience with image editing software (Photoshop, GIMP)",
      "Background in graphic design, photography, or visual arts",
      "Familiarity with annotation tools (Labelbox, CVAT, or similar)",
      "Knowledge of machine learning concepts",
      "Experience with transcription or data entry",
      "Multilingual abilities for international projects",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Steady Income",
        description:
          "Earn $18-30/hr with consistent work availability and weekly payments.",
      },
      {
        icon: "Clock",
        title: "Part-Time Flexibility",
        description:
          "Perfect for students, parents, or anyone seeking supplemental income with flexible hours.",
      },
      {
        icon: "MapPin",
        title: "Work From Home",
        description:
          "No commute required. Work comfortably from your home office or anywhere with internet.",
      },
      {
        icon: "Zap",
        title: "Quick Start",
        description:
          "Start earning within days of application. Our streamlined onboarding gets you working fast.",
      },
      {
        icon: "Award",
        title: "Skill Building",
        description:
          "Develop valuable skills in data analysis, quality assurance, and AI that transfer to other careers.",
      },
      {
        icon: "Gift",
        title: "Bonuses",
        description:
          "Earn performance bonuses for high accuracy and productivity on priority projects.",
      },
    ],
    workingConditions: [
      "Fully remote position with flexible scheduling",
      "Task-based work with clear completion criteria",
      "Variety of project types to prevent monotony",
      "Regular breaks encouraged for sustained accuracy",
      "Supportive team environment with accessible help",
      "Clear quality standards and immediate feedback",
    ],
    growthOpportunities: [
      "Senior Annotator: Handle complex projects with higher pay rates",
      "Quality Reviewer: Review and approve other annotators' work",
      "Annotation Specialist: Focus on specific data types (medical imaging, autonomous vehicles)",
      "Project Coordinator: Help manage annotation projects and teams",
      "Guidelines Developer: Create training materials and annotation standards",
      "Machine Learning Assistant: Transition to ML-focused roles",
    ],
    teamInfo: {
      size: "Join a team of 10,000+ annotators worldwide",
      structure:
        "Work independently with support from project managers and quality reviewers",
      collaboration:
        "Access to annotator forums, help channels, and regular Q&A sessions",
    },
    applicationProcess: [
      "Submit online application with basic information",
      "Complete a short skills assessment (visual acuity, attention to detail)",
      "Pass a sample annotation test for your preferred data type",
      "Complete online training modules (2-4 hours)",
      "Start with supervised practice tasks",
      "Graduate to independent work after quality verification",
    ],
    faqs: [
      {
        question: "What equipment do I need?",
        answer:
          "A computer with at least 8GB RAM, a high-resolution monitor, reliable internet, and headphones for audio tasks. We recommend a mouse for precise annotation work.",
      },
      {
        question: "Is the work repetitive?",
        answer:
          "While individual tasks may be similar, we offer variety through different project types. Many annotators find the work meditative and satisfying, especially when they see their contributions improving AI systems.",
      },
      {
        question: "How is quality measured?",
        answer:
          "We measure accuracy by comparing your annotations to expert-reviewed gold standard data. Consistency with guidelines and productivity are also tracked. Regular feedback helps you improve.",
      },
      {
        question: "Can I specialize in certain types of annotation?",
        answer:
          "Yes! After initial training, you can focus on image, text, audio, or video annotation based on your preferences and skills. Specialists often earn higher rates.",
      },
    ],
    estimatedHoursPerWeek: "10-30 hours (flexible)",
    projectTypes: [
      "Object detection and bounding boxes",
      "Semantic segmentation",
      "Image classification",
      "Named entity recognition",
      "Sentiment analysis",
      "Speech transcription",
      "Video object tracking",
    ],
    toolsUsed: [
      "Unimerg Annotation Platform",
      "Custom labeling interfaces",
      "Image annotation tools",
      "Audio transcription software",
      "Video annotation systems",
    ],
    trainingProvided: [
      "Platform orientation (1 hour)",
      "Data type-specific training (2-4 hours per type)",
      "Quality standards workshop",
      "Tool-specific tutorials",
      "Ongoing skill development resources",
    ],
    performanceMetrics: [
      "Annotation accuracy (vs. gold standard)",
      "Inter-annotator agreement",
      "Tasks completed per hour",
      "Error rate and revision frequency",
      "Guideline compliance score",
    ],
  },
  {
    id: "content-moderator",
    category: "Content Moderation",
    categoryColor: "mixed",
    title: "Content Moderator",
    payRange: "$20-35/hr",
    locationType: "Remote",
    schedule: "Flexible Hours",
    description:
      "Review and moderate user-generated content to ensure it meets community guidelines and safety standards across various platforms.",
    skills: ["Policy Knowledge", "Decision Making", "Cultural Awareness"],
    longDescription:
      "Content Moderators are the guardians of online communities. In this vital role, you'll review user-generated content across social media platforms, AI systems, and digital marketplaces to ensure safety, compliance, and positive user experiences. You'll make quick, accurate decisions about content that may violate policies, protecting users from harmful material while supporting free expression. This role requires emotional resilience, strong judgment, and a commitment to creating safer online spaces. Your decisions directly impact millions of users and help shape the standards for online communication worldwide.",
    overview:
      "As a Content Moderator, you'll review text, images, videos, and other user-generated content to determine if it violates platform policies. You'll handle sensitive material, make nuanced judgment calls, and help maintain safe online environments. This role is essential for protecting users and ensuring AI systems don't generate harmful content.",
    responsibilities: [
      {
        title: "Content Review",
        description:
          "Review user-generated content including text posts, images, videos, and comments against platform policies and community guidelines.",
      },
      {
        title: "Policy Enforcement",
        description:
          "Apply content policies consistently and fairly, making decisions on content removal, warnings, or escalation.",
      },
      {
        title: "AI Output Review",
        description:
          "Evaluate AI-generated content for safety, bias, and policy compliance, helping improve AI safety systems.",
      },
      {
        title: "Trend Identification",
        description:
          "Identify emerging trends in policy violations, new types of harmful content, and potential risks to users.",
      },
      {
        title: "Documentation",
        description:
          "Document decisions with clear reasoning, contributing to policy refinement and training materials.",
      },
      {
        title: "Escalation Handling",
        description:
          "Escalate complex or sensitive cases to senior moderators or specialized teams for review.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "High school diploma required; college degree preferred",
          "Previous experience in customer service, content review, or online community management helpful",
          "Familiarity with social media platforms and online communities",
          "Understanding of internet culture and current events",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Strong reading comprehension and quick assessment abilities",
          "Ability to navigate multiple software systems efficiently",
          "Basic understanding of content management systems",
          "Familiarity with various content formats (text, image, video)",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Excellent judgment and decision-making abilities",
          "Emotional resilience when viewing difficult content",
          "Cultural sensitivity and awareness of global perspectives",
          "Ability to remain objective and unbiased",
          "Strong ethics and integrity",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Reliable computer with updated browser",
          "High-speed internet connection (minimum 15 Mbps)",
          "Private, quiet workspace for handling sensitive content",
          "Availability for consistent scheduling (some projects require set hours)",
        ],
      },
    ],
    preferredQualifications: [
      "Degree in communications, psychology, sociology, or related field",
      "Previous content moderation experience",
      "Training in mental health first aid or crisis intervention",
      "Knowledge of legal frameworks around online content",
      "Multilingual abilities",
      "Experience with trust and safety operations",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Competitive Compensation",
        description:
          "Earn $20-35/hr with premium rates for specialized or high-volume content review.",
      },
      {
        icon: "Heart",
        title: "Wellness Support",
        description:
          "Access to mental health resources, counseling services, and wellness programs at no cost.",
      },
      {
        icon: "Clock",
        title: "Flexible Scheduling",
        description:
          "Choose shifts that work for your life, with various time slots available across time zones.",
      },
      {
        icon: "Shield",
        title: "Meaningful Impact",
        description:
          "Make a real difference in online safety, protecting users from harmful content.",
      },
      {
        icon: "Users",
        title: "Supportive Community",
        description:
          "Join a team that understands the challenges of this work and supports each other.",
      },
      {
        icon: "TrendingUp",
        title: "Career Pathway",
        description:
          "Clear advancement opportunities to senior moderator, team lead, and trust & safety roles.",
      },
    ],
    workingConditions: [
      "Remote work with flexible scheduling options",
      "Regular breaks required to maintain mental wellness",
      "Access to wellness resources and support",
      "Clear escalation paths for difficult content",
      "Supportive team environment",
      "Content exposure warnings and opt-out options for extreme content",
    ],
    growthOpportunities: [
      "Senior Content Moderator: Handle complex cases and mentor new moderators",
      "Team Lead: Manage a team of moderators and coordinate shifts",
      "Policy Specialist: Help develop and refine content policies",
      "Trust & Safety Analyst: Focus on platform-wide safety initiatives",
      "Quality Assurance: Review moderation decisions for accuracy",
      "Training Developer: Create training programs for new moderators",
    ],
    teamInfo: {
      size: "Part of a global moderation team of 3,000+ professionals",
      structure:
        "Teams organized by platform, content type, and time zone for 24/7 coverage",
      collaboration:
        "Regular team meetings, peer support channels, and access to mental health professionals",
    },
    applicationProcess: [
      "Submit application with background information",
      "Complete policy knowledge assessment",
      "Participate in scenario-based evaluation",
      "Pass background check (required for this role)",
      "Complete comprehensive training program (8-16 hours)",
      "Supervised moderation period before independent work",
    ],
    faqs: [
      {
        question: "Will I see disturbing content?",
        answer:
          "Content moderators may encounter disturbing material. We provide thorough training on handling such content, mandatory breaks, access to mental health support, and the ability to opt out of certain content categories.",
      },
      {
        question: "How do you support moderator mental health?",
        answer:
          "We offer free counseling services, regular wellness check-ins, peer support groups, mandatory break policies, and comprehensive mental health resources. Your wellbeing is our priority.",
      },
      {
        question: "Are there fixed schedules?",
        answer:
          "Some projects require coverage during specific hours, but we offer various shifts. You can indicate your preferred hours, and we'll match you with appropriate projects.",
      },
      {
        question: "How are edge cases handled?",
        answer:
          "When content is ambiguous, you can escalate to senior moderators or policy specialists. We encourage escalation over guessing—quality and consistency matter more than speed.",
      },
    ],
    estimatedHoursPerWeek: "20-40 hours",
    projectTypes: [
      "Social media content review",
      "AI safety evaluation",
      "Marketplace listing moderation",
      "Comment and chat moderation",
      "Image and video review",
      "Policy violation assessment",
    ],
    toolsUsed: [
      "Content moderation platforms",
      "Policy documentation systems",
      "Escalation and ticketing tools",
      "Communication platforms",
      "Reporting and analytics dashboards",
    ],
    trainingProvided: [
      "Platform policies deep dive (4-8 hours)",
      "Content handling and wellness training (4 hours)",
      "Tool and system training (2-4 hours)",
      "Cultural sensitivity training",
      "Ongoing policy updates and refreshers",
    ],
    performanceMetrics: [
      "Accuracy of moderation decisions",
      "Consistency with policy guidelines",
      "Review throughput (content per hour)",
      "Escalation appropriateness",
      "Quality audit scores",
    ],
  },
  {
    id: "linguistic-specialist",
    category: "Language Services",
    categoryColor: "purple",
    title: "Linguistic Specialist",
    payRange: "$30-50/hr",
    locationType: "Remote",
    schedule: "Contract",
    description:
      "Evaluate and improve AI language models by assessing grammar, context, tone, and cultural nuances across multiple languages.",
    skills: ["Multilingual", "Grammar Expertise", "Cultural Knowledge"],
    longDescription:
      "Linguistic Specialists are language experts who ensure AI systems communicate naturally and appropriately across cultures and languages. In this role, you'll leverage your deep understanding of language—including grammar, idioms, cultural context, and regional variations—to evaluate and improve AI-generated text. You'll work on projects ranging from translation quality assessment to dialect-specific training data creation. This role is ideal for linguists, translators, language teachers, and multilingual professionals who want to apply their expertise to shape how AI communicates with billions of users in their native languages.",
    overview:
      "As a Linguistic Specialist, you'll evaluate AI language capabilities, identify errors and areas for improvement, and help create training data that teaches AI systems to communicate naturally. Your expertise ensures AI respects cultural nuances and language variations that automated systems often miss.",
    responsibilities: [
      {
        title: "Language Quality Evaluation",
        description:
          "Assess AI-generated text for grammatical accuracy, natural flow, appropriate tone, and cultural sensitivity across languages.",
      },
      {
        title: "Translation Assessment",
        description:
          "Evaluate machine translation quality, identifying errors in meaning, grammar, and cultural appropriateness.",
      },
      {
        title: "Training Data Creation",
        description:
          "Create high-quality language samples in your native language(s) to train AI models on natural communication patterns.",
      },
      {
        title: "Cultural Localization",
        description:
          "Ensure AI content is appropriately localized for different regions, considering cultural norms and sensitivities.",
      },
      {
        title: "Dialect and Variant Work",
        description:
          "Help AI understand regional language variants, dialects, and informal communication styles.",
      },
      {
        title: "Error Analysis",
        description:
          "Document and categorize language errors to help engineering teams improve AI language models.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "Bachelor's degree in linguistics, languages, translation, or related field",
          "Native or near-native proficiency in at least one non-English language",
          "Professional experience in translation, interpretation, or language teaching",
          "Strong understanding of grammatical structures and language theory",
        ],
      },
      {
        category: "Language Skills",
        items: [
          "Native-level fluency in target language(s) with excellent written skills",
          "Deep understanding of cultural contexts and regional variations",
          "Ability to identify subtle grammatical and stylistic errors",
          "Knowledge of formal and informal registers",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Excellent attention to linguistic detail",
          "Cultural sensitivity and awareness",
          "Ability to explain language concepts clearly",
          "Patience for detailed, precise work",
          "Strong analytical and critical thinking skills",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Computer with reliable internet connection",
          "Ability to type efficiently in target language(s)",
          "Familiarity with language-specific keyboards and input methods",
          "Comfortable with web-based evaluation tools",
        ],
      },
    ],
    preferredQualifications: [
      "Master's degree or PhD in linguistics or related field",
      "Certification in translation (ATA, NAATI, or equivalent)",
      "Experience with computational linguistics or NLP",
      "Published work in linguistics or language studies",
      "Experience with CAT tools and translation technology",
      "Knowledge of multiple language families",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Premium Rates",
        description:
          "Earn $30-50/hr, with higher rates for rare languages and specialized expertise.",
      },
      {
        icon: "Globe",
        title: "Global Impact",
        description:
          "Help millions of people interact with AI in their native language.",
      },
      {
        icon: "BookOpen",
        title: "Intellectual Work",
        description:
          "Engage your expertise in meaningful, intellectually stimulating projects.",
      },
      {
        icon: "Clock",
        title: "Contract Flexibility",
        description:
          "Work on projects that match your schedule and language expertise.",
      },
      {
        icon: "Award",
        title: "Expert Recognition",
        description:
          "Be recognized as a language expert and contribute to cutting-edge AI research.",
      },
      {
        icon: "Users",
        title: "Professional Network",
        description:
          "Connect with linguists and language professionals worldwide.",
      },
    ],
    workingConditions: [
      "Fully remote with flexible project-based scheduling",
      "Projects matched to your specific language expertise",
      "Collaborative work with international teams",
      "Access to linguistic resources and references",
      "Regular communication with project managers",
      "Opportunity for long-term, ongoing engagement",
    ],
    growthOpportunities: [
      "Senior Linguistic Specialist: Lead complex multilingual projects",
      "Language Lead: Coordinate all work for a specific language",
      "Linguistic Consultant: Advise on language strategy and policy",
      "Training Developer: Create training materials for other linguists",
      "Research Contributor: Participate in linguistic research projects",
      "Localization Manager: Oversee localization for entire regions",
    ],
    teamInfo: {
      size: "Network of 2,000+ linguistic specialists covering 100+ languages",
      structure:
        "Specialists organized by language family and region, with dedicated project managers",
      collaboration:
        "Language-specific channels, regular specialist meetings, and collaborative annotation projects",
    },
    applicationProcess: [
      "Submit application with language credentials and experience",
      "Complete language proficiency assessment in your target language(s)",
      "Participate in a sample evaluation task",
      "Interview with linguistic team (for specialized projects)",
      "Complete onboarding and platform training",
      "Begin work on matched language projects",
    ],
    faqs: [
      {
        question: "What languages are in demand?",
        answer:
          "We have ongoing needs for all major world languages, with premium rates for less common languages like Swahili, Vietnamese, Thai, and various indigenous languages. Check our languages page for current priorities.",
      },
      {
        question: "Do I need formal linguistics training?",
        answer:
          "While a linguistics degree is preferred, we also value professional translators, language teachers, and native speakers with demonstrated language expertise and analytical abilities.",
      },
      {
        question: "Can I work on multiple languages?",
        answer:
          "Yes! If you're proficient in multiple languages, you can qualify for projects in each, potentially increasing your available work and earning potential.",
      },
      {
        question: "How technical is the work?",
        answer:
          "The work is linguistically technical but doesn't require programming skills. You'll focus on language quality, not code. We provide all necessary tools and training.",
      },
    ],
    estimatedHoursPerWeek: "10-30 hours (project-dependent)",
    projectTypes: [
      "Translation quality evaluation",
      "Grammar and fluency assessment",
      "Cultural appropriateness review",
      "Training data creation",
      "Dialect and variant documentation",
      "Conversational AI evaluation",
    ],
    toolsUsed: [
      "Unimerg Linguistic Platform",
      "Translation evaluation interfaces",
      "Glossary and terminology tools",
      "Collaborative editing platforms",
      "Language-specific reference resources",
    ],
    trainingProvided: [
      "Platform and tool orientation (2 hours)",
      "Evaluation methodology training (3-4 hours)",
      "Language-specific guidelines",
      "Cultural sensitivity training",
      "Ongoing calibration and quality sessions",
    ],
    performanceMetrics: [
      "Evaluation accuracy and consistency",
      "Inter-annotator agreement",
      "Error identification rate",
      "Cultural appropriateness scores",
      "Feedback quality and detail",
    ],
  },
  {
    id: "prompt-engineer",
    category: "AI Training",
    categoryColor: "purple",
    title: "Prompt Engineer",
    payRange: "$40-70/hr",
    locationType: "Remote",
    schedule: "Full Time",
    description:
      "Design and optimize prompts to improve AI model outputs, testing various approaches to achieve desired responses and behaviors.",
    skills: ["Creative Writing", "Technical Knowledge", "Problem Solving"],
    longDescription:
      "Prompt Engineers are the architects of AI communication. In this highly skilled role, you'll design, test, and optimize the prompts and instructions that shape how AI systems respond to users. You'll experiment with different approaches to elicit the best possible outputs, combining creativity with systematic testing to improve AI performance. This emerging field sits at the intersection of linguistics, psychology, and computer science, making it perfect for creative problem-solvers who enjoy both analytical and imaginative work. As AI becomes more prevalent, prompt engineering skills are increasingly valuable across industries.",
    overview:
      "Prompt Engineers craft the instructions and examples that guide AI behavior. You'll develop prompts that help AI systems understand user intent, generate appropriate responses, and handle edge cases gracefully. This role requires creativity, analytical thinking, and a deep understanding of how language influences AI outputs.",
    responsibilities: [
      {
        title: "Prompt Design",
        description:
          "Create effective prompts and system instructions that guide AI models to produce desired outputs across various use cases.",
      },
      {
        title: "Systematic Testing",
        description:
          "Design and execute experiments to test prompt effectiveness, measuring quality, consistency, and edge case handling.",
      },
      {
        title: "Optimization",
        description:
          "Iteratively refine prompts based on testing results, improving response quality while maintaining efficiency.",
      },
      {
        title: "Documentation",
        description:
          "Create comprehensive documentation of prompt strategies, best practices, and lessons learned.",
      },
      {
        title: "Collaboration",
        description:
          "Work with product teams to understand requirements and translate them into effective prompt strategies.",
      },
      {
        title: "Research",
        description:
          "Stay current with prompt engineering techniques, researching and testing new approaches as the field evolves.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "Bachelor's degree in computer science, linguistics, cognitive science, or related field",
          "2+ years experience working with AI/ML systems or natural language processing",
          "Demonstrated experience with large language models (GPT, Claude, etc.)",
          "Portfolio of prompt engineering work or related projects",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Deep understanding of how large language models work",
          "Experience with systematic testing and experimentation",
          "Strong analytical skills for evaluating AI outputs",
          "Familiarity with AI safety and alignment concepts",
          "Basic programming skills (Python preferred) for automation",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Exceptional written communication skills",
          "Creative and lateral thinking abilities",
          "Strong problem-solving and debugging skills",
          "Patience for iterative testing and refinement",
          "Ability to think from multiple user perspectives",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "High-performance computer for AI testing",
          "Reliable high-speed internet connection",
          "Ability to work during core collaboration hours",
          "Experience with version control (Git)",
        ],
      },
    ],
    preferredQualifications: [
      "Master's degree or PhD in relevant field",
      "Published work in NLP, AI, or related areas",
      "Experience at AI companies or research labs",
      "Knowledge of reinforcement learning from human feedback (RLHF)",
      "Experience with AI safety and red-teaming",
      "Background in UX writing or conversation design",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Top-Tier Compensation",
        description:
          "Earn $40-70/hr, among the highest rates in the AI training field, reflecting the specialized nature of this work.",
      },
      {
        icon: "Briefcase",
        title: "Full-Time Stability",
        description:
          "Enjoy consistent work with full-time hours and predictable income.",
      },
      {
        icon: "Rocket",
        title: "Cutting-Edge Work",
        description:
          "Work on the frontier of AI development, shaping how AI systems communicate and reason.",
      },
      {
        icon: "TrendingUp",
        title: "Career Advancement",
        description:
          "Build highly marketable skills in one of tech's fastest-growing specialties.",
      },
      {
        icon: "Users",
        title: "Expert Community",
        description:
          "Collaborate with top AI researchers and engineers from leading tech companies.",
      },
      {
        icon: "BookOpen",
        title: "Continuous Learning",
        description:
          "Access to AI research, conferences, and learning resources to stay at the cutting edge.",
      },
    ],
    workingConditions: [
      "Full-time remote position with core collaboration hours",
      "Work closely with AI research and engineering teams",
      "Fast-paced environment with evolving requirements",
      "Access to latest AI models and tools",
      "Regular team meetings and collaborative sessions",
      "Opportunities for research and publication",
    ],
    growthOpportunities: [
      "Senior Prompt Engineer: Lead complex prompt development projects",
      "Prompt Engineering Lead: Manage team of prompt engineers",
      "AI Research Scientist: Transition to AI research roles",
      "AI Product Manager: Lead AI product development",
      "AI Safety Specialist: Focus on alignment and safety",
      "Consultant: Provide prompt engineering expertise to enterprises",
    ],
    teamInfo: {
      size: "Selective team of 200+ expert prompt engineers",
      structure:
        "Collaborative pods working on specific AI systems and use cases",
      collaboration:
        "Daily standups, weekly deep-dives, and regular knowledge sharing sessions",
    },
    applicationProcess: [
      "Submit application with portfolio of prompt engineering work",
      "Complete technical assessment on prompt design and optimization",
      "Participate in practical prompt engineering challenge",
      "Technical interview with prompt engineering team",
      "Final interview with project leadership",
      "Comprehensive onboarding with team integration",
    ],
    faqs: [
      {
        question: "What does a typical day look like?",
        answer:
          "You'll spend time designing new prompts, testing variations systematically, analyzing results, documenting findings, and collaborating with team members on complex challenges. Expect a mix of creative and analytical work.",
      },
      {
        question: "How technical is this role?",
        answer:
          "While you don't need to be a software engineer, you should understand AI concepts deeply. Basic Python skills help for automation, but the focus is on language and systematic thinking rather than coding.",
      },
      {
        question: "Is this really a full-time role?",
        answer:
          "Yes! The complexity and depth of prompt engineering work supports full-time engagement. You'll work on multiple projects, conduct research, and continuously improve your craft.",
      },
      {
        question: "What's the career outlook?",
        answer:
          "Prompt engineering is one of the fastest-growing AI specialties. Skills developed here are highly transferable to AI product management, research, and consulting roles across industries.",
      },
    ],
    estimatedHoursPerWeek: "40 hours",
    projectTypes: [
      "System prompt development",
      "Few-shot learning optimization",
      "Chain-of-thought prompt design",
      "Safety and guardrail prompting",
      "Domain-specific prompt engineering",
      "Prompt testing and evaluation",
    ],
    toolsUsed: [
      "AI model APIs and playgrounds",
      "Prompt testing frameworks",
      "Version control (Git)",
      "Evaluation and metrics tools",
      "Documentation systems",
      "Collaboration platforms",
    ],
    trainingProvided: [
      "Advanced prompt engineering techniques (ongoing)",
      "AI model architecture understanding",
      "Testing methodology training",
      "Safety and alignment principles",
      "Research paper reading groups",
    ],
    performanceMetrics: [
      "Prompt effectiveness scores",
      "Response quality improvements",
      "Testing thoroughness",
      "Documentation quality",
      "Innovation and creative contributions",
    ],
  },
  {
    id: "quality-analyst",
    category: "Quality Assurance",
    categoryColor: "coral",
    title: "Quality Analyst",
    payRange: "$22-38/hr",
    locationType: "Remote",
    schedule: "Part Time",
    description:
      "Review and assess the quality of AI-generated content, ensuring accuracy, relevance, and adherence to project guidelines.",
    skills: ["Analytical Skills", "Quality Standards", "Documentation"],
    longDescription:
      "Quality Analysts are the gatekeepers of excellence at Unimerg. In this critical role, you'll review and assess the work of AI systems and human contributors, ensuring that all outputs meet our rigorous quality standards. You'll identify patterns in errors, provide constructive feedback, and help calibrate quality benchmarks across projects. This role combines analytical precision with mentorship, as your assessments help both AI and humans improve. If you have a keen eye for detail and take satisfaction in maintaining high standards, this role offers meaningful work that directly impacts AI quality worldwide.",
    overview:
      "Quality Analysts ensure that AI outputs and human contributions meet established standards. You'll review work samples, identify quality issues, provide feedback, and help maintain consistency across projects. This role is essential for ensuring that AI systems trained on our data perform reliably and accurately.",
    responsibilities: [
      {
        title: "Quality Review",
        description:
          "Evaluate AI-generated content and human contributions against quality rubrics, identifying issues and scoring accurately.",
      },
      {
        title: "Calibration",
        description:
          "Participate in calibration sessions to ensure consistent quality standards across the reviewer team.",
      },
      {
        title: "Feedback Provision",
        description:
          "Provide clear, constructive feedback that helps contributors understand and correct quality issues.",
      },
      {
        title: "Pattern Analysis",
        description:
          "Identify systematic quality issues and trends, reporting findings to improve processes and guidelines.",
      },
      {
        title: "Guideline Development",
        description:
          "Contribute to the development and refinement of quality guidelines and rubrics.",
      },
      {
        title: "Reporting",
        description:
          "Generate quality reports and metrics that inform project decisions and improvements.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "Bachelor's degree preferred, or equivalent professional experience",
          "Experience in quality assurance, editing, or review roles",
          "Background in content evaluation or academic assessment helpful",
          "Familiarity with rubric-based evaluation systems",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Strong analytical and evaluation capabilities",
          "Experience with quality metrics and measurement",
          "Proficiency with spreadsheets and data analysis",
          "Ability to learn and apply complex rubrics quickly",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Exceptional attention to detail",
          "Objective and unbiased judgment",
          "Clear written communication for feedback",
          "Consistency and reliability in assessments",
          "Ability to handle disagreement professionally",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Reliable computer and internet connection",
          "Comfortable with web-based review platforms",
          "Ability to maintain focus during detailed review work",
          "Availability for regular calibration sessions",
        ],
      },
    ],
    preferredQualifications: [
      "Previous experience in QA or quality control roles",
      "Background in education or assessment",
      "Experience with AI or machine learning projects",
      "Statistical analysis skills",
      "Process improvement experience",
      "Certification in quality assurance methodologies",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Solid Compensation",
        description:
          "Earn $22-38/hr for skilled analytical work with room for growth.",
      },
      {
        icon: "Clock",
        title: "Part-Time Flexibility",
        description: "Balance quality analysis work with other commitments.",
      },
      {
        icon: "Target",
        title: "Impactful Work",
        description:
          "Your reviews directly improve AI quality for millions of users.",
      },
      {
        icon: "Award",
        title: "Expertise Development",
        description:
          "Become an expert in AI quality assessment, a growing field.",
      },
      {
        icon: "TrendingUp",
        title: "Growth Path",
        description:
          "Advance to senior analyst, team lead, or quality management roles.",
      },
      {
        icon: "Users",
        title: "Team Collaboration",
        description:
          "Work with a dedicated team focused on maintaining excellence.",
      },
    ],
    workingConditions: [
      "Remote work with flexible scheduling",
      "Regular calibration sessions with team",
      "Clear quality standards and rubrics",
      "Supportive environment for questions",
      "Access to quality resources and tools",
      "Feedback on your own review quality",
    ],
    growthOpportunities: [
      "Senior Quality Analyst: Handle complex reviews and mentor others",
      "Quality Team Lead: Manage analyst team and coordinate reviews",
      "Quality Program Manager: Design quality programs across projects",
      "Rubric Developer: Create quality standards and evaluation criteria",
      "Training Specialist: Train new analysts on quality methods",
      "Quality Operations: Oversee quality at organizational level",
    ],
    teamInfo: {
      size: "Team of 500+ quality analysts globally",
      structure:
        "Analysts organized by project type with dedicated quality leads",
      collaboration:
        "Regular team calibrations, feedback sessions, and quality discussions",
    },
    applicationProcess: [
      "Submit application highlighting relevant experience",
      "Complete quality assessment skills test",
      "Participate in calibration exercise",
      "Interview with quality team",
      "Complete quality methodology training",
      "Begin supervised review work",
    ],
    faqs: [
      {
        question: "How is my own quality measured?",
        answer:
          "Your reviews are periodically compared to expert benchmarks and peer reviews. We measure accuracy, consistency, and the helpfulness of your feedback. Regular calibration ensures fair evaluation.",
      },
      {
        question: "What if I disagree with quality standards?",
        answer:
          "We encourage thoughtful disagreement! You can raise concerns through proper channels, and good suggestions often improve our guidelines. However, you should apply current standards while discussions are ongoing.",
      },
      {
        question: "Is the work monotonous?",
        answer:
          "Quality analysis requires sustained attention, but variety comes from different project types, evolving content, and the satisfaction of improving quality. Many analysts find it engaging intellectual work.",
      },
      {
        question: "How much feedback do I provide?",
        answer:
          "This varies by project—some require detailed written feedback on each item, others just require scores. We'll train you on expectations for each project type.",
      },
    ],
    estimatedHoursPerWeek: "15-25 hours",
    projectTypes: [
      "AI response quality review",
      "Human contribution assessment",
      "Guideline compliance checking",
      "Comparative quality evaluation",
      "Edge case identification",
      "Quality trend analysis",
    ],
    toolsUsed: [
      "Quality review platforms",
      "Scoring and feedback interfaces",
      "Analytics and reporting tools",
      "Calibration systems",
      "Documentation platforms",
    ],
    trainingProvided: [
      "Quality methodology fundamentals (4 hours)",
      "Rubric application training (2-4 hours per rubric)",
      "Feedback writing workshop",
      "Calibration best practices",
      "Ongoing quality refreshers",
    ],
    performanceMetrics: [
      "Inter-rater reliability",
      "Accuracy vs. expert benchmarks",
      "Review throughput",
      "Feedback quality scores",
      "Calibration alignment",
    ],
  },
  {
    id: "subject-matter-expert",
    category: "Expert Review",
    categoryColor: "mixed",
    title: "Subject Matter Expert",
    payRange: "$50-100/hr",
    locationType: "Remote",
    schedule: "Flexible Hours",
    description:
      "Provide domain expertise to evaluate and improve AI responses in specialized fields such as medicine, law, finance, or science.",
    skills: ["Domain Expertise", "Research Skills", "Technical Writing"],
    longDescription:
      "Subject Matter Experts bring specialized professional knowledge to AI development. In this prestigious role, you'll leverage your expertise in fields like medicine, law, finance, engineering, or science to evaluate AI performance in your domain. You'll identify factual errors, assess reasoning quality, and help ensure AI systems provide accurate, safe information in specialized areas where mistakes could have serious consequences. This role is ideal for professionals who want to apply their expertise to cutting-edge AI while earning premium compensation for their specialized knowledge.",
    overview:
      "Subject Matter Experts evaluate AI outputs in specialized domains where deep expertise is essential. Your professional knowledge helps identify errors, improve accuracy, and ensure AI systems are trustworthy for domain-specific applications. This work directly impacts how AI is used in critical fields like healthcare, legal services, and financial advice.",
    responsibilities: [
      {
        title: "Expert Evaluation",
        description:
          "Assess AI-generated content in your domain for accuracy, completeness, and appropriateness.",
      },
      {
        title: "Factual Verification",
        description:
          "Identify factual errors, outdated information, and potentially harmful advice in AI outputs.",
      },
      {
        title: "Quality Assessment",
        description:
          "Evaluate the quality of reasoning, citations, and explanations in domain-specific content.",
      },
      {
        title: "Training Data Creation",
        description:
          "Create expert-level content that serves as training examples for AI in your domain.",
      },
      {
        title: "Safety Review",
        description:
          "Identify outputs that could be harmful or dangerous if acted upon in real-world scenarios.",
      },
      {
        title: "Consultation",
        description:
          "Advise on domain-specific guidelines, edge cases, and best practices for AI development.",
      },
    ],
    requirements: [
      {
        category: "Education & Credentials",
        items: [
          "Advanced degree in your field (Master's, MD, JD, PhD, etc.) or equivalent expertise",
          "Active professional credentials or licensure where applicable",
          "Minimum 5 years of professional experience in your domain",
          "Track record of expertise (publications, practice, recognition)",
        ],
      },
      {
        category: "Domain Knowledge",
        items: [
          "Deep, current knowledge of your professional field",
          "Understanding of best practices and standards",
          "Awareness of common misconceptions and errors",
          "Knowledge of ethical considerations in your field",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Ability to explain complex concepts clearly",
          "Objective evaluation skills",
          "Attention to accuracy and detail",
          "Good judgment about severity of errors",
          "Professional integrity",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Reliable computer and internet access",
          "Ability to allocate focused time for review work",
          "Comfortable with web-based platforms",
          "Access to professional references and resources",
        ],
      },
    ],
    preferredQualifications: [
      "Current active practice in your field",
      "Teaching or educational experience",
      "Publication record in peer-reviewed outlets",
      "Experience with AI or technology in your field",
      "Consulting or advisory experience",
      "Leadership roles in professional organizations",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Premium Compensation",
        description:
          "Earn $50-100+/hr—rates that reflect the value of specialized expertise.",
      },
      {
        icon: "Clock",
        title: "Flexible Engagement",
        description:
          "Work on your schedule, fitting expert review around your primary professional commitments.",
      },
      {
        icon: "Shield",
        title: "Meaningful Impact",
        description:
          "Ensure AI systems are safe and accurate in your field, protecting future users.",
      },
      {
        icon: "Award",
        title: "Professional Recognition",
        description:
          "Contribute to cutting-edge AI development in your domain.",
      },
      {
        icon: "BookOpen",
        title: "Stay Current",
        description:
          "Engage with how AI is transforming your field, building relevant expertise.",
      },
      {
        icon: "Users",
        title: "Expert Network",
        description: "Connect with other leading experts across disciplines.",
      },
    ],
    workingConditions: [
      "Fully flexible, project-based engagement",
      "Work matches your specific expertise",
      "Clear scope and expectations for each project",
      "Support from project managers and AI teams",
      "Professional treatment and confidentiality",
      "Option for ongoing or occasional engagement",
    ],
    growthOpportunities: [
      "Lead Expert: Coordinate expert review in your subdomain",
      "Domain Advisor: Shape AI strategy in your field",
      "Guidelines Developer: Create domain-specific evaluation criteria",
      "Research Collaborator: Participate in AI research projects",
      "Advisory Board: Provide strategic guidance on AI development",
      "Consulting Roles: Expand to broader AI consulting",
    ],
    teamInfo: {
      size: "Network of 1,000+ verified experts across domains",
      structure:
        "Experts organized by field with dedicated project coordinators",
      collaboration:
        "Domain-specific expert groups, periodic consultations, and collaborative projects",
    },
    applicationProcess: [
      "Submit application with credentials and CV",
      "Credential verification process",
      "Complete domain-specific expert assessment",
      "Brief interview with domain coordinator",
      "Sign expert agreement and NDAs",
      "Begin receiving matched expert projects",
    ],
    faqs: [
      {
        question: "What domains are you looking for?",
        answer:
          "We need experts across many fields: medicine (all specialties), law, finance, engineering, science (all disciplines), education, psychology, and more. If you're a recognized expert, we likely have relevant work.",
      },
      {
        question: "How much time commitment is required?",
        answer:
          "There's no minimum—some experts review a few items monthly, others work several hours weekly. You control your availability and can adjust based on your schedule.",
      },
      {
        question: "How is my expertise verified?",
        answer:
          "We verify credentials through official sources, check professional standing, and may conduct brief interviews. This protects the integrity of expert review and justifies premium rates.",
      },
      {
        question: "Can I continue my regular professional work?",
        answer:
          "Absolutely. Most experts maintain their primary careers while contributing to Unimerg. Our flexible model is designed to complement, not replace, your professional life.",
      },
    ],
    estimatedHoursPerWeek: "2-20 hours (highly flexible)",
    projectTypes: [
      "Factual accuracy verification",
      "Professional advice assessment",
      "Domain-specific training data creation",
      "Safety and harm evaluation",
      "Expert guideline development",
      "Specialized consultation",
    ],
    toolsUsed: [
      "Expert review platform",
      "Secure evaluation interfaces",
      "Domain-specific resources",
      "Collaboration tools",
      "Confidential documentation systems",
    ],
    trainingProvided: [
      "Platform orientation (1 hour)",
      "Evaluation methodology overview (2 hours)",
      "Domain-specific guidelines",
      "Safety and ethics review",
      "Ongoing updates on AI capabilities",
    ],
    performanceMetrics: [
      "Evaluation accuracy and depth",
      "Error identification rate",
      "Response quality and clarity",
      "Availability and reliability",
      "Peer expert agreement",
    ],
  },
  {
    id: "conversation-designer",
    category: "AI Training",
    categoryColor: "purple",
    title: "Conversation Designer",
    payRange: "$35-55/hr",
    locationType: "Remote",
    schedule: "Contract",
    description:
      "Create natural, engaging dialogue flows for conversational AI systems, ensuring intuitive and helpful user interactions.",
    skills: ["UX Writing", "Dialogue Design", "User Empathy"],
    longDescription:
      "Conversation Designers craft the personality and interaction patterns of AI assistants. In this creative role, you'll design dialogue flows that feel natural, helpful, and engaging, shaping how millions of users experience AI. You'll consider user psychology, conversation dynamics, and brand voice to create interactions that delight users while achieving their goals efficiently. This role combines UX design principles with writing craft, making it ideal for professionals who understand both the art and science of great conversation.",
    overview:
      "Conversation Designers create the scripts, flows, and personality guidelines that govern how AI assistants communicate. You'll design conversations that handle diverse user needs, recover gracefully from misunderstandings, and maintain consistency across interactions. Your work directly shapes user satisfaction with AI products.",
    responsibilities: [
      {
        title: "Dialogue Flow Design",
        description:
          "Create conversation flows that guide users naturally toward their goals while handling variations and edge cases.",
      },
      {
        title: "Persona Development",
        description:
          "Define and maintain AI assistant personalities, including tone, style, and character traits.",
      },
      {
        title: "Sample Dialogue Creation",
        description:
          "Write diverse example dialogues that train AI on natural conversation patterns.",
      },
      {
        title: "Error Handling Design",
        description:
          "Design graceful recovery patterns for misunderstandings, errors, and unexpected user inputs.",
      },
      {
        title: "User Research",
        description:
          "Analyze user conversation data to identify pain points and opportunities for improvement.",
      },
      {
        title: "Cross-functional Collaboration",
        description:
          "Work with product, engineering, and AI teams to implement conversation designs.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "Bachelor's degree in UX design, linguistics, communications, or related field",
          "3+ years experience in UX writing, conversation design, or related roles",
          "Portfolio demonstrating conversation design work",
          "Experience with chatbots, voice assistants, or conversational AI",
        ],
      },
      {
        category: "Design Skills",
        items: [
          "Strong understanding of conversation design principles",
          "Ability to create clear, logical dialogue flows",
          "Experience with user research and usability testing",
          "Knowledge of accessibility in conversational interfaces",
        ],
      },
      {
        category: "Writing Skills",
        items: [
          "Exceptional writing ability with range of tones and styles",
          "Ability to write concise, clear dialogue",
          "Understanding of how written text sounds when spoken",
          "Experience writing for diverse audiences",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Familiarity with conversation design tools",
          "Understanding of how AI language models work",
          "Ability to collaborate in remote design processes",
          "Comfortable with iterative design and feedback",
        ],
      },
    ],
    preferredQualifications: [
      "Experience at a major tech company or AI startup",
      "Background in screenwriting, playwriting, or dialogue writing",
      "Knowledge of linguistics or cognitive science",
      "Experience with voice interface design",
      "Understanding of prompt engineering",
      "Multilingual abilities for international projects",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Strong Rates",
        description:
          "Earn $35-55/hr for creative, skilled work that shapes AI experiences.",
      },
      {
        icon: "Palette",
        title: "Creative Work",
        description:
          "Apply your creative talents to one of technology's most exciting frontiers.",
      },
      {
        icon: "Users",
        title: "Massive Impact",
        description:
          "Design conversations that millions of people will have every day.",
      },
      {
        icon: "Clock",
        title: "Contract Flexibility",
        description:
          "Project-based work that fits your schedule and creative energy.",
      },
      {
        icon: "TrendingUp",
        title: "Growing Field",
        description:
          "Build expertise in conversation design, a rapidly expanding specialty.",
      },
      {
        icon: "Lightbulb",
        title: "Innovation",
        description:
          "Work on novel interaction patterns that define the future of human-AI communication.",
      },
    ],
    workingConditions: [
      "Remote, project-based contract work",
      "Collaborative design processes with teams",
      "Creative freedom within project guidelines",
      "Regular feedback and iteration cycles",
      "Access to user research and conversation data",
      "Opportunities for long-term engagement",
    ],
    growthOpportunities: [
      "Senior Conversation Designer: Lead complex conversation design projects",
      "Design Lead: Manage conversation design teams",
      "Product Designer: Expand to broader AI product design",
      "AI Experience Director: Shape overall AI user experience",
      "Consultant: Provide conversation design expertise to clients",
      "Author/Educator: Teach and write about conversation design",
    ],
    teamInfo: {
      size: "Team of 10+ conversation designers",
      structure:
        "Designers work in project pods with product managers and AI engineers",
      collaboration:
        "Regular design reviews, critique sessions, and knowledge sharing",
    },
    applicationProcess: [
      "Submit application with portfolio of conversation design work",
      "Complete conversation design challenge",
      "Portfolio review and interview",
      "Paid trial project",
      "Contract finalization",
      "Begin project work",
    ],
    faqs: [
      {
        question: "Do I need technical AI knowledge?",
        answer:
          "You should understand how conversational AI works conceptually, but you don't need to code. We'll provide training on AI capabilities and limitations that affect design decisions.",
      },
      {
        question: "What makes a strong portfolio?",
        answer:
          "We look for evidence of user-centered design thinking, range of conversation types, graceful error handling, and personality development. Include process documentation alongside finished work.",
      },
      {
        question: "How collaborative is the work?",
        answer:
          "Very! You'll work closely with product managers, AI engineers, and other designers. Strong collaboration skills are essential.",
      },
      {
        question: "What's the difference between this and UX writing?",
        answer:
          "Conversation design focuses specifically on dialogue flows and back-and-forth interactions, while UX writing often covers broader interface text. Many skills overlap, but conversation design is more dynamic.",
      },
    ],
    estimatedHoursPerWeek: "20-35 hours (project-dependent)",
    projectTypes: [
      "Dialogue flow design",
      "Persona development",
      "Error handling patterns",
      "Onboarding conversations",
      "Multi-turn interaction design",
      "Voice and chat adaptation",
    ],
    toolsUsed: [
      "Conversation design platforms",
      "Flow diagramming tools",
      "Prototyping software",
      "User research tools",
      "AI testing interfaces",
    ],
    trainingProvided: [
      "AI capabilities and limitations overview (3 hours)",
      "Project-specific design guidelines",
      "Persona and style guides",
      "Platform-specific training",
      "Regular design methodology workshops",
    ],
    performanceMetrics: [
      "Conversation completion rates",
      "User satisfaction scores",
      "Design quality reviews",
      "Iteration efficiency",
      "Stakeholder feedback",
    ],
  },
  {
    id: "image-analyst",
    category: "Data Annotation",
    categoryColor: "coral",
    title: "Image Analyst",
    payRange: "$20-35/hr",
    locationType: "Remote",
    schedule: "Part Time",
    description:
      "Analyze and label visual content including objects, scenes, and actions to train computer vision models for various applications.",
    skills: ["Visual Analysis", "Attention to Detail", "Categorization"],
    longDescription:
      "Image Analysts are the eyes that teach AI to see. In this visually-focused role, you'll analyze images and videos, identifying and labeling objects, scenes, actions, and attributes that help computer vision systems understand visual content. Your work enables AI applications from autonomous vehicles to medical imaging to accessibility features for the visually impaired. This role is ideal for detail-oriented individuals with strong visual perception who enjoy working with images and want to contribute to groundbreaking visual AI applications.",
    overview:
      "Image Analysts provide the labeled visual data that trains computer vision AI. You'll work with diverse image types—photographs, medical scans, satellite imagery, video frames—applying labels and annotations that teach AI to recognize and understand visual content. Your precision directly impacts AI performance in safety-critical applications.",
    responsibilities: [
      {
        title: "Object Annotation",
        description:
          "Draw precise bounding boxes, polygons, and segmentation masks around objects in images.",
      },
      {
        title: "Classification",
        description:
          "Categorize images and image regions by content, scene type, quality, and other attributes.",
      },
      {
        title: "Keypoint Annotation",
        description:
          "Mark specific points on objects (joints, landmarks, features) for pose estimation and alignment.",
      },
      {
        title: "Action Recognition",
        description:
          "Identify and label actions, activities, and events in images and video sequences.",
      },
      {
        title: "Quality Verification",
        description:
          "Review annotations for accuracy and consistency, flagging issues for correction.",
      },
      {
        title: "Edge Case Documentation",
        description:
          "Identify and document unusual or ambiguous images that require special handling.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "High school diploma required; college education preferred",
          "No prior annotation experience required—we provide training",
          "Experience with visual detail work (design, photography, etc.) helpful",
          "Familiarity with image editing software is a plus",
        ],
      },
      {
        category: "Visual Skills",
        items: [
          "Excellent visual perception and attention to detail",
          "Ability to identify objects and boundaries precisely",
          "Color vision sufficient for image analysis tasks",
          "Spatial reasoning and understanding of perspective",
        ],
      },
      {
        category: "Soft Skills",
        items: [
          "Patience and precision for detailed work",
          "Consistency in applying guidelines across many images",
          "Self-motivation and focus",
          "Good judgment for ambiguous cases",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Computer with high-resolution display (1080p minimum, 4K preferred)",
          "Accurate color-calibrated monitor for color-sensitive work",
          "Mouse or drawing tablet for precise annotation",
          "Reliable high-speed internet connection",
        ],
      },
    ],
    preferredQualifications: [
      "Background in photography, graphic design, or visual arts",
      "Experience with image editing (Photoshop, GIMP, etc.)",
      "Familiarity with computer vision concepts",
      "Experience with annotation tools (CVAT, Labelbox, VGG, etc.)",
      "Medical imaging experience for healthcare projects",
      "GIS experience for satellite imagery projects",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Good Pay",
        description:
          "Earn $20-35/hr for skilled visual analysis work, with bonuses for accuracy.",
      },
      {
        icon: "Eye",
        title: "Interesting Work",
        description:
          "Work with diverse images from interesting domains—nature, cities, products, and more.",
      },
      {
        icon: "Clock",
        title: "Part-Time Fit",
        description: "Flexible hours that work around other commitments.",
      },
      {
        icon: "Zap",
        title: "Quick Feedback",
        description:
          "See the results of your work quickly with immediate quality feedback.",
      },
      {
        icon: "TrendingUp",
        title: "Skill Growth",
        description:
          "Develop valuable skills in computer vision annotation and analysis.",
      },
      {
        icon: "Shield",
        title: "Meaningful Impact",
        description:
          "Your work enables AI that helps autonomous vehicles drive safely, assists doctors with diagnoses, and more.",
      },
    ],
    workingConditions: [
      "Remote work with flexible scheduling",
      "Task-based work with clear quality criteria",
      "Variety of image types and domains",
      "Regular breaks encouraged for visual fatigue",
      "Clear feedback on annotation quality",
      "Supportive team environment",
    ],
    growthOpportunities: [
      "Senior Image Analyst: Handle complex annotation projects",
      "Specialty Analyst: Focus on medical, satellite, or automotive imagery",
      "Quality Reviewer: Review and approve image annotations",
      "Annotation Lead: Coordinate image annotation teams",
      "Training Developer: Create training for visual annotation",
      "Computer Vision Assistant: Transition to ML-focused roles",
    ],
    teamInfo: {
      size: "Team of 3,000+ image analysts worldwide",
      structure: "Analysts organized by project type and specialty",
      collaboration:
        "Project channels for questions, regular Q&A sessions, peer support",
    },
    applicationProcess: [
      "Submit online application",
      "Complete visual skills assessment",
      "Pass annotation accuracy test",
      "Complete platform training (3-4 hours)",
      "Begin supervised annotation work",
      "Graduate to independent projects",
    ],
    faqs: [
      {
        question: "What types of images will I work with?",
        answer:
          "It varies by project—general photography, street scenes for autonomous vehicles, product images, satellite imagery, medical scans (with training), and more. You can develop expertise in specific domains.",
      },
      {
        question: "How precise do annotations need to be?",
        answer:
          "Very precise. For object detection, bounding boxes should tightly fit objects. For segmentation, pixel-level accuracy is expected. We provide detailed guidelines and quality targets for each project.",
      },
      {
        question: "Will I see disturbing images?",
        answer:
          "Most projects involve benign imagery. Projects involving potentially disturbing content are clearly marked, optional, and come with support resources. You can choose your comfort level.",
      },
      {
        question: "How can I work faster without losing accuracy?",
        answer:
          "Speed comes with practice. We provide tips on efficient annotation techniques, keyboard shortcuts, and workflow optimization. Accuracy is always prioritized over speed.",
      },
    ],
    estimatedHoursPerWeek: "10-25 hours",
    projectTypes: [
      "Object detection and localization",
      "Semantic and instance segmentation",
      "Image classification",
      "Keypoint and landmark annotation",
      "Video object tracking",
      "3D annotation and depth estimation",
    ],
    toolsUsed: [
      "Unimerg Image Annotation Platform",
      "Specialized annotation interfaces",
      "Quality review tools",
      "Reference image libraries",
      "Collaboration platforms",
    ],
    trainingProvided: [
      "Annotation fundamentals (2 hours)",
      "Tool-specific training (2-3 hours)",
      "Project-specific guidelines",
      "Accuracy improvement workshops",
      "Specialty domain training (as needed)",
    ],
    performanceMetrics: [
      "Annotation accuracy (IoU scores)",
      "Consistency with guidelines",
      "Productivity (images per hour)",
      "Error rate",
      "Quality review scores",
    ],
  },
  {
    id: "audio-transcriptionist",
    category: "Data Annotation",
    categoryColor: "coral",
    title: "Audio Transcriptionist",
    payRange: "$18-28/hr",
    locationType: "Remote",
    schedule: "Flexible Hours",
    description:
      "Transcribe and annotate audio content to help train speech recognition and natural language processing models.",
    skills: ["Listening Skills", "Typing Speed", "Language Proficiency"],
    longDescription:
      "Audio Transcriptionists convert speech to text, providing the essential training data for voice assistants, transcription services, and accessibility tools. In this listening-focused role, you'll transcribe conversations, interviews, meetings, and other audio content with high accuracy. You'll also annotate audio with speaker identification, timestamps, and other metadata that helps AI understand not just what was said, but how it was said. This role is perfect for excellent listeners with strong language skills who enjoy the meditative focus of detailed transcription work.",
    overview:
      "Audio Transcriptionists create accurate text transcriptions of audio content that train speech recognition AI. You'll listen carefully to recordings, capture speech accurately, and add annotations that help AI understand context. Your work enables voice assistants, automatic transcription, and accessibility features for deaf and hard-of-hearing users.",
    responsibilities: [
      {
        title: "Transcription",
        description:
          "Convert audio recordings to accurate text, capturing all spoken content including filler words, false starts, and non-verbal sounds as specified.",
      },
      {
        title: "Speaker Identification",
        description:
          "Identify and label different speakers in multi-party recordings.",
      },
      {
        title: "Timestamp Annotation",
        description:
          "Add precise timestamps to transcriptions for alignment with audio.",
      },
      {
        title: "Metadata Tagging",
        description:
          "Label audio characteristics like emotion, emphasis, background noise, and audio quality.",
      },
      {
        title: "Quality Review",
        description:
          "Review and correct transcriptions for accuracy and consistency.",
      },
      {
        title: "Specialized Transcription",
        description:
          "Handle domain-specific content (medical, legal, technical) with appropriate terminology.",
      },
    ],
    requirements: [
      {
        category: "Education & Experience",
        items: [
          "High school diploma required",
          "Transcription experience preferred but not required",
          "Native-level proficiency in target language",
          "Experience with audio equipment helpful",
        ],
      },
      {
        category: "Technical Skills",
        items: [
          "Typing speed of 60+ WPM with high accuracy",
          "Excellent listening comprehension",
          "Familiarity with transcription conventions",
          "Ability to use transcription software and foot pedals",
        ],
      },
      {
        category: "Language Skills",
        items: [
          "Perfect grammar and spelling in target language",
          "Understanding of regional accents and dialects",
          "Knowledge of specialized terminology (or willingness to learn)",
          "Punctuation and formatting expertise",
        ],
      },
      {
        category: "Technical Requirements",
        items: [
          "Quality headphones (noise-isolating recommended)",
          "Quiet work environment",
          "Reliable computer and internet",
          "Foot pedal recommended for efficiency",
        ],
      },
    ],
    preferredQualifications: [
      "Professional transcription certification",
      "Experience with medical or legal transcription",
      "Background in linguistics or language studies",
      "Multilingual abilities",
      "Experience with court reporting or closed captioning",
      "Knowledge of phonetic notation",
    ],
    benefits: [
      {
        icon: "DollarSign",
        title: "Steady Pay",
        description:
          "Earn $18-28/hr with higher rates for specialized content and difficult audio.",
      },
      {
        icon: "Clock",
        title: "Flexible Hours",
        description:
          "Work when you want—morning, evening, or late night. You set your schedule.",
      },
      {
        icon: "Headphones",
        title: "Interesting Content",
        description:
          "Listen to diverse content—interviews, podcasts, meetings, lectures, and more.",
      },
      {
        icon: "Home",
        title: "Work From Home",
        description:
          "No commute, no office—work comfortably from your own space.",
      },
      {
        icon: "TrendingUp",
        title: "Speed Improvement",
        description:
          "Improve your typing speed and accuracy with regular practice.",
      },
      {
        icon: "Globe",
        title: "Enable Accessibility",
        description:
          "Your work helps create transcripts and captions that make content accessible to all.",
      },
    ],
    workingConditions: [
      "Fully remote with complete schedule flexibility",
      "Task-based work—complete at your own pace",
      "Variety of audio types and subjects",
      "Clear quality standards and feedback",
      "Quiet work environment required",
      "Break-friendly work structure",
    ],
    growthOpportunities: [
      "Senior Transcriptionist: Handle complex and specialized audio",
      "Quality Reviewer: Review transcriptions for accuracy",
      "Specialty Transcriptionist: Focus on medical, legal, or technical content",
      "Team Lead: Coordinate transcription projects",
      "Captioning Specialist: Real-time or broadcast captioning",
      "Training Developer: Create transcription training materials",
    ],
    teamInfo: {
      size: "Network of 2,000+ transcriptionists covering multiple languages",
      structure: "Transcriptionists organized by language and specialty",
      collaboration:
        "Help channels for difficult audio, style guide discussions, peer support",
    },
    applicationProcess: [
      "Submit application with typing speed certification",
      "Complete listening comprehension assessment",
      "Pass transcription accuracy test",
      "Complete platform and style guide training",
      "Begin supervised transcription work",
      "Graduate to independent projects",
    ],
    faqs: [
      {
        question: "What equipment do I need?",
        answer:
          "Good quality headphones are essential—noise-isolating or over-ear types work best. A foot pedal for playback control significantly increases efficiency and is recommended for serious transcriptionists.",
      },
      {
        question: "How is transcription quality measured?",
        answer:
          "We measure word error rate (WER) against verified transcripts. Accuracy of 98%+ is expected for clear audio. We also assess proper formatting, punctuation, and adherence to style guides.",
      },
      {
        question: "What about difficult audio with accents or noise?",
        answer:
          "Difficult audio is flagged and may pay premium rates. We provide guidelines for handling unclear speech. You can mark inaudible sections rather than guessing.",
      },
      {
        question: "How fast do I need to transcribe?",
        answer:
          "Speed varies by content difficulty. Real-time factor (RTF) of 4:1 (4 minutes to transcribe 1 minute of audio) is typical for clear content. Quality is always prioritized over speed.",
      },
    ],
    estimatedHoursPerWeek: "10-30 hours (flexible)",
    projectTypes: [
      "Verbatim transcription",
      "Clean/edited transcription",
      "Speaker diarization",
      "Timestamped transcription",
      "Phonetic transcription",
      "Multilingual and code-switched audio",
    ],
    toolsUsed: [
      "Unimerg Transcription Platform",
      "Audio playback tools with speed control",
      "Text editors with auto-formatting",
      "Speaker identification tools",
      "Quality review interfaces",
    ],
    trainingProvided: [
      "Transcription standards and style guide (3 hours)",
      "Platform and tool training (2 hours)",
      "Specialty content training (as needed)",
      "Accuracy improvement workshops",
      "Ongoing quality feedback",
    ],
    performanceMetrics: [
      "Word error rate (WER)",
      "Transcription speed (RTF)",
      "Format compliance",
      "Timestamp accuracy",
      "Quality review scores",
    ],
  },
];
